import { AuthoringMigrationAction } from "./authoring-migration.jsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { idempotencyKey, routeHash } from "./helpers.mjs";
import { canManagePlatformSettings, canEnableReadySolution, readySolutionPublicationIntent, publishReadySolution, setReadySolutionEnabled } from "./ready-solution-controls.mjs";
import { CounterpartyPilot } from "./counterparty-v2.jsx";
import { ReadySolutionModelCatalog } from "./ready-solution-model-catalog.jsx";
import { COUNTERPARTY_ADMIN_SOLUTION_ID, READY_SOLUTION_ADMIN_TABS, readySolutionAdminHref, readySolutionAdminView } from "./ready-solution-admin.mjs";
import { ReadySolutionStatus, ReadySolutionTable } from "./ready-solution-table.jsx";
import { counterpartyPublication, readLatestSolutionRun, readySolutionStatus } from "./ready-solution-status.mjs";

export function PlatformSettingsNavigation({ session, current, tab }) {
  if (!canManagePlatformSettings(session)) return null;
  const active = current === "ready-solutions" ? readySolutionAdminView(tab) : current || "organizations";
  const links = [
    ["organizations", "Организации", routeHash("settings", "organizations")],
    ...READY_SOLUTION_ADMIN_TABS.map(([id, label]) => [id, id === "solutions" ? "Готовые решения" : label, readySolutionAdminHref(id)]),
    ...[["beta", "Бесплатная бета"], ["users", "Пользователи"], ["budget", "Бюджет"]].map(([id, label]) => [id, label, routeHash("settings", id)]),
  ];
  return <nav className="section-nav" aria-label="Настройки платформы">
    {links.map(([id, label, href]) =>
      <a key={id} className={active === id ? "active" : ""} aria-current={active === id ? "page" : undefined} href={href}>{label}</a>)}
    <a href="/console/admin/roadmap">План развития</a>
  </nav>;
}

const modelRoleName = (role) => ({ analysis: "Анализ", review: "Проверка", generator: "Генерация", generator_1: "Генератор 1", generator_2: "Генератор 2", reviewer: "Проверка", researcher: "Исследование", composer: "Сборка", validator: "Проверка" }[role] || role);

export function ReadySolutionCard({ item, busy, publishing, onPublish, onToggle }) {
  const supported = item.supported === true;
  const toggleDisabled = busy || !supported || (!item.enabled && !canEnableReadySolution(item));
  return <article className="ready-solution-management">
    <header><div><h3>{item.name}</h3><p className="muted">{item.description}</p></div>
      <label className="ready-solution-availability"><input type="checkbox" role="switch" checked={Boolean(item.enabled)} disabled={toggleDisabled} onChange={(event) => onToggle(event.target.checked)} aria-label={`Доступ к решению «${item.name}»`}/><span>{item.enabled ? "Включено" : "Отключено"}</span></label>
    </header>
    <p className="muted">{item.message || (item.ready ? "Версия опубликована." : "Сначала опубликуйте версию решения.")}</p>
    {item.ready && <dl className="ready-solution-versions"><div><dt>Версия агента</dt><dd>{item.agentVersion}</dd></div>{item.modelProfileVersion && <div><dt>Версия настроек моделей</dt><dd>{item.modelProfileVersion}</dd></div>}</dl>}
    {item.ready && item.modelBindings?.length > 0 && <div className="ready-solution-models"><h4>Модели опубликованной версии</h4><ul>{item.modelBindings.map((binding, index) => <li key={`${binding.role}-${index}`}><span>{modelRoleName(binding.role)}</span><strong>{binding.model} · {binding.provider}</strong></li>)}</ul></div>}
    {supported ? <div className="ready-solution-publish"><button type="button" className="button ghost" disabled={busy || !(item.deploymentRevision > 0)} onClick={onPublish}>{publishing ? "Публикуем…" : "Опубликовать текущие настройки"}</button><small>Используются сохранённые модели. Публикация не запускает решение.</small></div> : <p className="muted">Управление доступом для этого решения пока не поддерживается.</p>}
  </article>;
}

export function PlatformReadySolutions({ api, onCatalogChanged, tab, selectedAgent, selectedSolution = "" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const view = readySolutionAdminView(tab);
  const [models, setModels] = useState({ items: [], loading: true, error: null });
  const modelRequest = useRef(0);
  const publicationIntents = useRef({});
  const [counterparty, setCounterparty] = useState({});
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [executions, setExecutions] = useState({});
  const rows = useMemo(() => [
    ...items.filter((item) => item.solutionId !== COUNTERPARTY_ADMIN_SOLUTION_ID).map((item) => ({ ...item, version: item.revision > 0 ? item.agentVersion : "" })),
    counterpartyPublication(counterparty),
  ], [items, counterparty]);
  const runKey = JSON.stringify(rows.filter((item) => !item.loading && !item.loadError).map((item) => [item.solutionId, item.version || ""]));
  useEffect(() => {
    let cancelled = false;
    const solutionIds = JSON.parse(runKey).map(([id]) => id);
    setExecutions(Object.fromEntries(solutionIds.map((id) => [id, { loading: true }])));
    Promise.all(solutionIds.map(async (id) => {
      try {
        const result = await readLatestSolutionRun(api, id);
        if (!cancelled) setExecutions((current) => ({ ...current, [id]: result }));
      } catch (failure) {
        if (!cancelled) setExecutions((current) => ({ ...current, [id]: { error: failure.message || "Не удалось загрузить историю." } }));
      }
    }));
    return () => { cancelled = true; };
  }, [api, runKey, refreshRevision]);
  const loadModels = useCallback(async () => {
    const request = ++modelRequest.current;
    setModels((current) => ({ ...current, loading: true, error: null }));
    try {
      const result = await api.request("/v1/platform/ready-solutions/models");
      if (request === modelRequest.current) setModels({ ...result, items: result.items || [], loading: false, error: null });
    } catch (error) {
      if (request === modelRequest.current) setModels((current) => ({ ...current, loading: false, error }));
    }
  }, [api]);
  useEffect(() => {
    setModels({ items: [], loading: true, error: null });
    loadModels();
    return () => { ++modelRequest.current; };
  }, [loadModels]);
  const saveModels = async (revision, selection) => {
    const result = await api.request("/v1/platform/ready-solutions/models", { method: "PUT", body: { revision, models: selection } });
    ++modelRequest.current;
    setModels({ ...result, items: result.items || [], loading: false, error: null });
  };
  const load = useCallback(async () => {
    setLoading(true); setError(""); setNotice("");
    try { const result = await api.request("/v1/platform/ready-solutions"); setItems(result.items); publicationIntents.current = {}; }
    catch (failure) { setError(failure.message || "Не удалось загрузить решения."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const publish = async (item) => {
    setBusy(`publish:${item.solutionId}`); setError(""); setNotice("");
    const intent = readySolutionPublicationIntent(item, publicationIntents.current[item.solutionId], idempotencyKey);
    publicationIntents.current[item.solutionId] = intent;
    try {
      const result = await publishReadySolution(api, item, intent); setItems(result.items); delete publicationIntents.current[item.solutionId];
      setNotice(`Текущие настройки решения «${item.name}» опубликованы.`);
      await onCatalogChanged?.();
    } catch (failure) { setError(failure.message || "Не удалось опубликовать решение. Повторите или обновите сведения."); }
    finally { setBusy(""); }
  };
  const toggle = async (item, enabled) => {
    setBusy(`toggle:${item.solutionId}`); setError(""); setNotice("");
    try {
      const result = await setReadySolutionEnabled(api, item, enabled); setItems(result.items);
      setNotice(`Решение «${item.name}» ${enabled ? "включено" : "отключено"}.`);
      await onCatalogChanged?.();
    } catch (failure) { setError(failure.message || "Не удалось изменить доступ. Обновите сведения перед повтором."); }
    finally { setBusy(""); }
  };
  const refresh = () => { load(); setRefreshRevision((value) => value + 1); };
  const selectedItem = rows.find((item) => item.solutionId === selectedSolution);
  const selectedLegacy = items.find((item) => item.solutionId === selectedSolution && item.solutionId !== COUNTERPARTY_ADMIN_SOLUTION_ID);
  const showDetails = view === "solutions" && Boolean(selectedSolution);
  return <section className="surface platform-ready-solutions">
    <div className="section-heading"><div><h2>Готовые решения</h2><p className="muted">Настройте агентов и опубликуйте решения для одобренных организаций.</p></div><button type="button" className="button ghost" disabled={loading || Boolean(busy)} onClick={refresh}>Обновить решения</button></div>
    <div hidden={view !== "models"}><ReadySolutionModelCatalog catalog={models} onRetry={loadModels} onSave={saveModels}/></div>
    <div hidden={view !== "solutions"}>
    {error && <p className="inline-problem" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {showDetails ? <>
      <a className="text-link" href={readySolutionAdminHref()}>← Все решения</a>
      {selectedItem && <AuthoringMigrationAction api={api} solutionID={selectedItem.solutionId} location={{catalog:"platform"}} compact/>}
      {selectedItem ? <p className="ready-solution-detail-status"><ReadySolutionStatus status={readySolutionStatus(selectedItem, executions[selectedSolution])}/></p> : !loading && <p role="alert">Решение не найдено.</p>}
      {selectedLegacy && <ReadySolutionCard item={selectedLegacy} busy={Boolean(busy)} publishing={busy === `publish:${selectedSolution}`} onPublish={() => publish(selectedLegacy)} onToggle={(enabled) => toggle(selectedLegacy, enabled)}/>}
    </> : <>
      {counterparty.unsaved && <p className="counterparty-warning">Есть несохранённые изменения справки v2. Откройте её настройки или карточку агента для сохранения.</p>}
      {loading ? <p className="muted">Загружаем решения…</p> : <ReadySolutionTable items={rows} executions={executions}/>}
    </>}
    </div>
    <div hidden={view === "models" || (view === "solutions" && selectedSolution !== COUNTERPARTY_ADMIN_SOLUTION_ID)}><CounterpartyPilot api={api} adminView={view} selectedAgent={selectedAgent} onCatalogChanged={onCatalogChanged} models={models} onReloadModels={loadModels} onAdminStateChange={setCounterparty} refreshRevision={refreshRevision}/></div>
  </section>;
}
